import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPasswordChecks,
  normalizeRegistrationPayload,
  validateRegistration,
} from "./registrationValidation";

const markAllTouched = (values) =>
  Object.keys(values).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

export default function useRegistrationForm({
  initialValues,
  mode,
  t,
}) {
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordChecks = useMemo(
    () => getPasswordChecks(values.password || ""),
    [values.password]
  );

  const validateValues = useCallback(
    (nextValues = values) => validateRegistration(nextValues, mode, t),
    [mode, t, values]
  );

  useEffect(() => {
    setFieldErrors((prevErrors) => {
      if (!Object.keys(prevErrors).length) return prevErrors;

      const localizedErrors = validateValues(values);
      const nextErrors = { ...prevErrors };

      Object.keys(prevErrors).forEach((name) => {
        nextErrors[name] = localizedErrors[name] || prevErrors[name];
      });

      return nextErrors;
    });
  }, [validateValues, values]);

  const setFieldValue = (name, value) => {
    setValues((prev) => {
      const nextValues = { ...prev, [name]: value };
      if (submitAttempted || touched[name]) {
        const nextErrors = validateValues(nextValues);
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          [name]: nextErrors[name] || "",
          ...(name === "password" ? { confirmPassword: nextErrors.confirmPassword || "" } : {}),
        }));
      }
      return nextValues;
    });
  };

  const handleBlur = (name) => {
    setTouched((prev) => {
      const nextTouched = { ...prev, [name]: true };
      const nextErrors = validateValues(values);
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [name]: nextErrors[name] || "",
      }));
      return nextTouched;
    });
  };

  const validateAll = (nextValues = values) => {
    const errors = validateValues(nextValues);
    setSubmitAttempted(true);
    setTouched(markAllTouched(nextValues));
    setFieldErrors(errors);
    return errors;
  };

  const resetFieldErrors = (names) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      names.forEach((name) => {
        delete next[name];
      });
      return next;
    });
  };

  return {
    values,
    setValues,
    touched,
    fieldErrors,
    setFieldErrors,
    submitAttempted,
    isSubmitting,
    setIsSubmitting,
    passwordChecks,
    handleBlur,
    setFieldValue,
    validateAll,
    resetFieldErrors,
    normalizePayload: (nextValues = values) => normalizeRegistrationPayload(nextValues, mode),
  };
}
